import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Save } from "lucide-react";
import { toast } from "sonner";
import {
  listServerGroups,
  removeServerGroupMember,
  renameServerGroup,
  searchGameServers,
  upsertGameServer,
  upsertServerGroup,
} from "@/api/games";
import type {
  GameServerEntry,
  ServerGroupEntry,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { QueryView } from "@/components/ui/QueryView";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";

export function ServerGroupsPanel({ gameID }: { gameID: string }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["server-groups", gameID],
    queryFn: () => listServerGroups(gameID),
  });
  const [serverSearch, setServerSearch] = useState("");
  const serversQ = useQuery({
    queryKey: ["game-servers", gameID, serverSearch],
    queryFn: () => searchGameServers(gameID, serverSearch, 10),
    staleTime: 15_000,
  });
  const servers = serversQ.data?.items ?? [];

  const upsertMut = useMutation({
    mutationFn: (input: { canonical_server_id: string; members: string[]; note: string }) =>
      upsertServerGroup(gameID, input),
    onSuccess: () => {
      toast.success("已保存区服组");
      void queryClient.invalidateQueries({ queryKey: ["server-groups", gameID] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });
  const removeMut = useMutation({
    mutationFn: (memberID: string) => removeServerGroupMember(gameID, memberID),
    onSuccess: () => {
      toast.success("已移除成员");
      void queryClient.invalidateQueries({ queryKey: ["server-groups", gameID] });
    },
    onError: (e) => toast.error(`移除失败：${(e as Error).message}`),
  });
  const renameMut = useMutation({
    mutationFn: (input: { canonicalID: string; newCanonicalID: string }) =>
      renameServerGroup(gameID, input.canonicalID, input.newCanonicalID),
    onSuccess: () => {
      toast.success("已重命名归一区服");
      void queryClient.invalidateQueries({ queryKey: ["server-groups", gameID] });
      void queryClient.invalidateQueries({ queryKey: ["map-respawn-stats", gameID] });
    },
    onError: (e) => toast.error(`重命名失败：${(e as Error).message}`),
  });
  const serverMut = useMutation({
    mutationFn: (input: {
      server_id: string;
      server_name: string;
      display_name: string;
      alias: string;
      metadata_json: string;
    }) => upsertGameServer(gameID, input),
    onSuccess: (data) => {
      toast.success("已保存区服目录");
      void queryClient.invalidateQueries({ queryKey: ["game-servers", gameID] });
      if (data.server.server_id) {
        void queryClient.invalidateQueries({ queryKey: ["map-respawn-stats", gameID, data.server.server_id] });
      }
    },
    onError: (e) => toast.error(`保存区服失败：${(e as Error).message}`),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>区服目录</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[340px_1fr]">
          <GameServerForm
            submitting={serverMut.isPending}
            onSubmit={(input) => serverMut.mutate(input)}
          />
          <GameServerDirectoryPanel
            servers={servers}
            loading={serversQ.isFetching}
            error={serversQ.isError ? (serversQ.error as Error).message : undefined}
            search={serverSearch}
            onSearchChange={setServerSearch}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>合区组</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[340px_1fr]">
          <ServerGroupForm
            submitting={upsertMut.isPending}
            onSubmit={(input) => upsertMut.mutate(input)}
          />
          <QueryView query={q}>
          {(data) => (
            <ServerGroupTable
              groups={data.groups}
              removing={removeMut.isPending}
              renaming={renameMut.isPending}
              onRemove={(memberID) => removeMut.mutate(memberID)}
              onRename={(canonicalID, newCanonicalID) =>
                renameMut.mutate({ canonicalID, newCanonicalID })
              }
            />
          )}
          </QueryView>
        </CardContent>
      </Card>

    </div>
  );
}

function GameServerForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (input: {
    server_id: string;
    server_name: string;
    display_name: string;
    alias: string;
    metadata_json: string;
  }) => void;
}) {
  const [serverID, setServerID] = useState("");
  const [serverName, setServerName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [alias, setAlias] = useState("");
  const [note, setNote] = useState("");

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const metadata = note.trim() ? JSON.stringify({ note: note.trim() }) : "{}";
    onSubmit({
      server_id: serverID.trim(),
      server_name: serverName.trim(),
      display_name: displayName.trim(),
      alias: alias.trim(),
      metadata_json: metadata,
    });
  }

  return (
    <form className="space-y-3" onSubmit={submit} noValidate>
          <Field label="区服 ID">
            <Input
              value={serverID}
              onChange={(e) => setServerID(e.target.value)}
              placeholder="24"
            />
          </Field>
          <Field label="游戏上报名称">
            <Input
              value={serverName}
              onChange={(e) => setServerName(e.target.value)}
              placeholder="二十四区"
            />
          </Field>
          <Field label="展示名">
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="主力服"
            />
          </Field>
          <Field label="别名">
            <Input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="新区, 推荐"
            />
          </Field>
          <Field label="备注">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <Button size="sm" className="gap-1" disabled={submitting || serverID.trim() === ""}>
            <Save className="size-3.5" aria-hidden="true" />
            {submitting ? "保存中..." : "保存区服"}
          </Button>
    </form>
  );
}

function ServerGroupForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (input: { canonical_server_id: string; members: string[]; note: string }) => void;
}) {
  const [canonical, setCanonical] = useState("");
  const [members, setMembers] = useState("");
  const [note, setNote] = useState("");

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit({
      canonical_server_id: canonical.trim(),
      members: splitServerIDs(members),
      note: note.trim(),
    });
  }

  return (
    <form className="space-y-3" onSubmit={submit} noValidate>
          <Field label="归一区服 ID">
            <Input
              value={canonical}
              onChange={(e) => setCanonical(e.target.value)}
              placeholder="24"
            />
          </Field>
          <Field label="成员区服 ID">
            <Textarea
              rows={5}
              value={members}
              onChange={(e) => setMembers(e.target.value)}
              placeholder="24, 25, 26"
            />
          </Field>
          <Field label="备注">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <Button size="sm" className="gap-1" disabled={submitting || canonical.trim() === ""}>
            <Save className="size-3.5" aria-hidden="true" />
            {submitting ? "保存中..." : "保存区服组"}
          </Button>
    </form>
  );
}

function ServerGroupTable({
  groups,
  removing,
  renaming,
  onRemove,
  onRename,
}: {
  groups: ServerGroupEntry[];
  removing: boolean;
  renaming: boolean;
  onRemove: (memberID: string) => void;
  onRename: (canonicalID: string, newCanonicalID: string) => void;
}) {
  if (groups.length === 0) {
    return (
      <div className="rounded border border-dashed border-[hsl(var(--border))] p-4 text-sm text-[hsl(var(--muted-foreground))]">
        还没有配置区服组，未配置区服会按自身 ID 作为归一区服。
      </div>
    );
  }
  return (
    <div className="min-w-0 overflow-x-auto">
      <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead>归一区服</TableHead>
              <TableHead>成员</TableHead>
              <TableHead>备注</TableHead>
              <TableHead>操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <ServerGroupRow
                key={group.canonical_server_id}
                group={group}
                removing={removing}
                renaming={renaming}
                onRemove={onRemove}
                onRename={onRename}
              />
            ))}
          </TableBody>
        </Table>
    </div>
  );
}

function ServerGroupRow({
  group,
  removing,
  renaming,
  onRemove,
  onRename,
}: {
  group: ServerGroupEntry;
  removing: boolean;
  renaming: boolean;
  onRemove: (memberID: string) => void;
  onRename: (canonicalID: string, newCanonicalID: string) => void;
}) {
  const [renamingTo, setRenamingTo] = useState(group.canonical_server_id);
  return (
    <TableRow>
      <TableCell className="font-mono">{group.canonical_server_id}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {group.members.map((member) => (
            <span
              key={member}
              className="inline-flex items-center gap-1 rounded border border-[hsl(var(--border))] px-2 py-0.5 font-mono"
            >
              {member}
              <button
                type="button"
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
                disabled={removing}
                onClick={() => onRemove(member)}
                aria-label={`移除 ${member}`}
              >
                x
              </button>
            </span>
          ))}
        </div>
      </TableCell>
      <TableCell>
        {group.note || <span className="text-[hsl(var(--muted-foreground))]">-</span>}
      </TableCell>
      <TableCell>
        <form
          className="flex min-w-52 items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onRename(group.canonical_server_id, renamingTo.trim());
          }}
        >
          <Input
            value={renamingTo}
            onChange={(e) => setRenamingTo(e.target.value)}
            className="h-8 font-mono text-xs"
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={
              renaming ||
              renamingTo.trim() === "" ||
              renamingTo.trim() === group.canonical_server_id
            }
          >
            <Pencil className="size-3.5" aria-hidden="true" />
            重命名
          </Button>
        </form>
      </TableCell>
    </TableRow>
  );
}

function GameServerDirectoryPanel({
  servers,
  loading,
  error,
  search,
  onSearchChange,
}: {
  servers: GameServerEntry[];
  loading: boolean;
  error?: string;
  search: string;
  onSearchChange: (next: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)] lg:items-end">
        <Field
          label="搜索区服"
          hint={
            loading
              ? "搜索中..."
              : error ||
                "空搜索显示最近目录；可按区服 ID、上报名、展示名或别名搜索。"
          }
        >
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="区服名 / server_id"
          />
        </Field>
      </div>
      <GameServerDirectoryTable
        items={servers}
        loading={loading}
      />
    </div>
  );
}

function GameServerDirectoryTable({
  items,
  loading,
}: {
  items: GameServerEntry[];
  loading: boolean;
}) {
  if (items.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {loading ? "正在读取区服目录..." : "目录中暂无匹配区服。Lua 上报新 server_id 后会自动补入目录。"}
      </p>
    );
  }
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>区服</TableHead>
          <TableHead>别名</TableHead>
          <TableHead>主区</TableHead>
          <TableHead>来源</TableHead>
          <TableHead>角色</TableHead>
          <TableHead>首次出现</TableHead>
          <TableHead>最近出现</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((server) => (
          <TableRow key={server.server_id}>
            <TableCell>
              <div className="font-medium">
                {server.display_name || server.server_name || server.server_id}
              </div>
              <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
                {server.server_id}
                {server.display_name && server.server_name ? ` · ${server.server_name}` : ""}
              </div>
            </TableCell>
            <TableCell>{server.alias || "-"}</TableCell>
            <TableCell className="font-mono">{server.effective_main_server_id || server.server_id || "-"}</TableCell>
            <TableCell>{formatServerSource(server.source)}</TableCell>
            <TableCell>{server.role_count}</TableCell>
            <TableCell>{formatTime(server.first_seen_at ?? 0)}</TableCell>
            <TableCell>{formatTime(server.last_seen_at ?? 0)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function formatServerSource(source?: string) {
  switch (source) {
    case "manual":
      return "人工";
    case "import":
      return "导入";
    case "lua_seen":
      return "Lua";
    case "python_cdp":
      return "CDP";
    case "market_cdp":
      return "CDP";
    default:
      return source || "-";
  }
}

function splitServerIDs(raw: string) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw.split(/[\s,，、]+/)) {
    const value = item.trim();
    if (value && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return out;
}

function formatTime(ts: number) {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
}
